// src/components/store/AffiliateTracker.tsx
"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

interface AffiliateTrackerProps {
  subdomain: string;
}

function readCookieValue(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : "";
}

export function getStoredAffiliateAttribution(subdomain: string) {
  if (typeof window === "undefined") {
    return { ref: "", click_token: "", signed_token: "", campaign_id: "" };
  }
  const candidateKeys = [
    `xylo_affiliate_attr_${subdomain}`,
    "xylo_affiliate_attr_global",
  ];
  for (const key of candidateKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.expires_at && new Date(parsed.expires_at).getTime() < Date.now()) {
          localStorage.removeItem(key);
          continue;
        }
        if (parsed.referral_code || parsed.ref) {
          return {
            ref: parsed.referral_code || parsed.ref || "",
            click_token: parsed.click_token || "",
            signed_token: parsed.signed_token || "",
            campaign_id: parsed.campaign_id || "",
          };
        }
      }
    } catch {}
  }

  const cookieRef = readCookieValue("xylo_aff_ref");
  if (cookieRef) {
    return {
      ref: cookieRef,
      click_token: readCookieValue("xylo_aff_click"),
      signed_token: readCookieValue("xylo_aff_sig"),
      campaign_id: "",
    };
  }

  return { ref: "", click_token: "", signed_token: "", campaign_id: "" };
}

export function saveAffiliateAttributionLocally(
  subdomain: string,
  payload: {
    referral_code: string;
    click_token?: string;
    signed_token?: string;
    campaign_id?: string;
    attribution_model?: string;
    cookie_duration_days?: number;
    expires_at?: string;
  }
) {
  if (typeof window === "undefined" || !payload.referral_code) return;
  const maxAgeDays = Number(payload.cookie_duration_days) || 30;
  const record = {
    referral_code: payload.referral_code.trim().toUpperCase(),
    click_token: payload.click_token || "",
    signed_token: payload.signed_token || "",
    campaign_id: payload.campaign_id || "",
    attribution_model: payload.attribution_model || "last_click",
    expires_at:
      payload.expires_at ||
      new Date(Date.now() + maxAgeDays * 86400 * 1000).toISOString(),
  };

  try {
    localStorage.setItem(`xylo_affiliate_attr_${subdomain}`, JSON.stringify(record));
    localStorage.setItem("xylo_affiliate_attr_global", JSON.stringify(record));
  } catch {}

  const maxAgeSec = maxAgeDays * 86400;
  document.cookie = `xylo_aff_ref=${encodeURIComponent(record.referral_code)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
  if (record.click_token) {
    document.cookie = `xylo_aff_click=${encodeURIComponent(record.click_token)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
  }
  if (record.signed_token) {
    document.cookie = `xylo_aff_sig=${encodeURIComponent(record.signed_token)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
  }
}

export default function AffiliateTracker({ subdomain }: AffiliateTrackerProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === "undefined" || !searchParams) return;

    const refParam =
      searchParams.get("ref") ||
      searchParams.get("referral_code") ||
      searchParams.get("affiliate");
    if (!refParam) return;

    const linkCode = searchParams.get("lnk") || "";
    const sigToken = searchParams.get("sig") || "";
    const campId = searchParams.get("camp") || "";
    const utmSource = searchParams.get("utm_source") || "direct";

    let productSlug = "";
    if (pathname && pathname.includes("/products/")) {
      const parts = pathname.split("/products/");
      if (parts[1]) {
        productSlug = decodeURIComponent(parts[1].split("/")[0].split("?")[0]);
      }
    }

    // Save immediate optimistic attribution so instant "Buy Now" never misses the ref code
    saveAffiliateAttributionLocally(subdomain, {
      referral_code: refParam.trim().split(".")[0],
      signed_token: sigToken,
      campaign_id: campId,
    });

    const host = window.location.hostname;
    const isLocal = host.includes("localhost") || host === "127.0.0.1";
    const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";

    const trackKey = `xylo_aff_tracked_${subdomain}_${refParam}_${linkCode}_${productSlug}`;
    if (sessionStorage.getItem(trackKey)) return;
    sessionStorage.setItem(trackKey, "1");

    const trackReferral = async () => {
      try {
        const res = await fetch(`${apiBase}/api/v1/affiliates/track/?subdomain=${encodeURIComponent(subdomain)}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Tenant-Subdomain": subdomain,
          },
          credentials: "include",
          body: JSON.stringify({
            ref: refParam.trim(),
            lnk: linkCode,
            sig: sigToken,
            camp: campId,
            utm_source: utmSource,
            product_slug: productSlug,
            path: pathname || "/",
          }),
        });

        if (res.ok) {
          const data = await res.json();
          if (data?.tracked && data?.referral_code) {
            saveAffiliateAttributionLocally(subdomain, {
              referral_code: data.referral_code,
              click_token: data.click_token || "",
              signed_token: data.signed_token || sigToken || "",
              campaign_id: campId || "",
              attribution_model: data.attribution_model || "last_click",
              cookie_duration_days: Number(data.cookie_duration_days) || 30,
              expires_at: data.expires_at,
            });
          }
        }
      } catch {}
    };

    trackReferral();
  }, [pathname, searchParams, subdomain]);

  return null;
}